import { type FC, useEffect } from 'react'
import { FormControlLabel } from '@mui/material'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import { Box, Card, StackColumn, Body1, type Theme } from '@dhl-ui/components'
import { WarningYellow } from '@dhl/icons'
import { CheckboxInput } from '@dhl/inputs'
import { useNativeBridge } from '@dhl/native-bridge'
import {
  usePostNotificationPreferencesEmailNotificationPreferences,
  usePostNotificationPreferencesPushNotificationPreferences,
} from '@dhl/receiver-profile-api'

import { PhoneComputerIllustration } from '@dhl-ui/illustrations'
import { useGetProfileEmail } from '~/profile/model/profile/hooks.js'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { steps } from '~/setup/model/steps.js'
import { StepDescription } from '../StepDescription.js'
import { StepForm } from '../StepForm.js'

type FormValues = {
  allPushNotificationsEnabled: boolean
  pushNotificationsIncomingParcelsEnabled: boolean
  pushNotificationsOutgoingParcelsEnabled: boolean
  emailNotificationEnabled: boolean
}

const warningBoxStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.secondary.light,
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const NotificationPreferenceForm = () => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<FormValues>()

  const noNotificationPreferenceSet =
    !values.pushNotificationsIncomingParcelsEnabled && !values.emailNotificationEnabled

  useEffect(() => {
    setFieldValue('pushNotificationsIncomingParcelsEnabled', values.allPushNotificationsEnabled)
    setFieldValue('pushNotificationsOutgoingParcelsEnabled', values.allPushNotificationsEnabled)
  }, [values.allPushNotificationsEnabled, setFieldValue])

  useEffect(() => {
    if (values.pushNotificationsIncomingParcelsEnabled && values.pushNotificationsOutgoingParcelsEnabled) {
      setFieldValue('allPushNotificationsEnabled', true)
    } else if (!values.pushNotificationsIncomingParcelsEnabled && !values.pushNotificationsOutgoingParcelsEnabled) {
      setFieldValue('allPushNotificationsEnabled', false)
    }
  }, [values.pushNotificationsIncomingParcelsEnabled, values.pushNotificationsOutgoingParcelsEnabled, setFieldValue])

  return (
    <StackColumn gap={3}>
      <Card>
        <Card.Header title={t('portal.profileSetup.notificationPreference.pushNotificationsCardTitle')} />
        <Card.Content>
          <StackColumn gap={2}>
            <Body1>{t('portal.profileSetup.notificationPreference.pushNotificationsCardDescription')}</Body1>
            <FormControlLabel
              sx={{
                gap: 1,
                alignItems: { xs: 'flex-start', md: 'center' },
                height: 'fit-content !important',
              }}
              control={<CheckboxInput name="pushNotificationsIncomingParcelsEnabled" />}
              data-testid="notification-preference-push-incoming"
              label={t('portal.profileSetup.notificationPreference.pushNotificationIncomingParcelsLabel')}
            />
            <FormControlLabel
              sx={{
                gap: 1,
                alignItems: { xs: 'flex-start', md: 'center' },
                height: 'fit-content !important',
              }}
              control={<CheckboxInput name="pushNotificationsOutgoingParcelsEnabled" />}
              data-testid="notification-preference-push-outgoing"
              label={t('portal.profileSetup.notificationPreference.pushNotificationOutgoingParcelsLabel')}
            />
          </StackColumn>
        </Card.Content>
      </Card>

      <Card>
        <Card.Header title={t('portal.profileSetup.notificationPreference.emailNotificationCardTitle')} />
        <Card.Content>
          <StackColumn gap={2}>
            <Body1>{t('portal.profileSetup.notificationPreference.emailNotificationCardDescription')}</Body1>
            <FormControlLabel
              sx={{
                gap: 1,
                alignItems: { xs: 'flex-start', md: 'center' },
                height: 'fit-content !important',
              }}
              control={<CheckboxInput name="emailNotificationEnabled" />}
              data-testid="notification-preference-email"
              label={t('portal.profileSetup.notificationPreference.emailNotificationLabel')}
            />
          </StackColumn>
        </Card.Content>
      </Card>

      {noNotificationPreferenceSet && (
        <Box>
          <Box sx={warningBoxStyles}>
            <WarningYellow />
            <Body1 data-testid="no-notification-preference-set-warning">
              {t('portal.profileSetup.notificationPreference.noNotificationPreferenceSetWarning')}
            </Body1>
          </Box>
        </Box>
      )}
    </StackColumn>
  )
}

export const NotificationPreference: FC = () => {
  const { t } = useTranslation()
  const { data: primaryEmailAddress } = useGetProfileEmail()
  const { onStepComplete, setStepSubmittedValues, getStepSubmittedValues } = useWizardContext()

  useSetTitle(t('portal.profileSetup.notificationPreference.pageTitle'))
  const { mutateAsync: updateEmailNotificationPreference } =
    usePostNotificationPreferencesEmailNotificationPreferences()
  const { mutateAsync: updatePushNotificationPreference } = usePostNotificationPreferencesPushNotificationPreferences()
  const { requestPushNotificationPermissions } = useNativeBridge()

  const previouslySubmittedValues = getStepSubmittedValues<FormValues>(steps.notificationPreference)

  const initialValues: FormValues = previouslySubmittedValues ?? {
    allPushNotificationsEnabled: true,
    pushNotificationsIncomingParcelsEnabled: true,
    pushNotificationsOutgoingParcelsEnabled: true,
    emailNotificationEnabled: true,
  }

  const handleSubmit = async ({
    emailNotificationEnabled,
    pushNotificationsIncomingParcelsEnabled,
    pushNotificationsOutgoingParcelsEnabled,
  }: FormValues) => {
    if (!primaryEmailAddress) {
      return
    }

    if (pushNotificationsIncomingParcelsEnabled || pushNotificationsOutgoingParcelsEnabled) {
      requestPushNotificationPermissions()
    }

    await updatePushNotificationPreference({
      data: {
        enabledForIncomingParcels: pushNotificationsIncomingParcelsEnabled,
        enabledForOutgoingParcels: pushNotificationsOutgoingParcelsEnabled,
      },
    })

    await updateEmailNotificationPreference({
      data: {
        emailAddress: primaryEmailAddress,
        enabled: emailNotificationEnabled,
      },
    })

    setStepSubmittedValues(steps.notificationPreference, {
      allPushNotificationsEnabled: pushNotificationsIncomingParcelsEnabled && pushNotificationsOutgoingParcelsEnabled,
      pushNotificationsIncomingParcelsEnabled,
      pushNotificationsOutgoingParcelsEnabled,
      emailNotificationEnabled,
    })
    onStepComplete()
  }

  return (
    <StackColumn gap={3}>
      <StepDescription
        slotProps={{ illustration: { variant: PhoneComputerIllustration } }}
        title={t('portal.profileSetup.notificationPreference.stepTitle')}
        description={t('portal.profileSetup.notificationPreference.stepDescription')}
      />

      <StepForm initialValues={initialValues} onSubmit={handleSubmit}>
        <NotificationPreferenceForm />
      </StepForm>
    </StackColumn>
  )
}
